import { ReactNode, useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Box, Container, Dialog, Flex } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { useRootStore } from 'core/hooks/use-root-store';
import NavBar from 'Components/element/NavBar';
import Footer from 'Components/element/Footer';
import { GlobalLoader } from 'shared/components/global-loader';
import { CandidateSidebar } from 'candidate/candidate-sidebar';
import { ResumeLockScreen } from 'candidate/core/resume-lock-screen/resume-lock-screen';
import { CandidateModuleStore } from 'candidate/core/candidate-module.store';

const containerSize = "1550px"

const CandidateRoutesGuardView = () => {
   const rootStore = useRootStore();
   const location = useLocation();
   const navigate = useNavigate();
   const { t } = useTranslation('global');
   const candidateModule = useMemo(
      () => new CandidateModuleStore(rootStore, navigate, t),
      [rootStore, navigate, t]
   );

   useEffect(() => {
      candidateModule.handleResumeLockRouteState(location);
   }, [candidateModule, location]);

   function render(): ReactNode {
      if (rootStore.userStore.isInit) {
         return (
            <Box width="100%" height="100vh">
               <GlobalLoader loaderStore={rootStore.globalLoader} />
            </Box>
         );
      }

      if (!rootStore.userStore.user) {
         return <Navigate to="/" replace />;
      }

      if (rootStore.userStore.user && rootStore.userStore.user.user_type !== 'candidate') {
         return <Navigate to="/auth/candidate-login" replace />;
      }

      return <Outlet />;
   }

   return (
      <>
         <NavBar />
         <Container className='candidate-layout' my="8" px="5" maxWidth={containerSize}>
            <Flex
               direction={{
                  md: 'row',
                  "initial": "column"
               }}
               gap="6"
            >
               <Box minWidth="300px">
                  <CandidateSidebar store={candidateModule} />
               </Box>

               <Box width="100%" overflow="hidden">
                  {render()}
               </Box>
            </Flex>
         </Container>

         <Footer />

         <Dialog.Root
            open={candidateModule.resumeLockDialogOpen}
            onOpenChange={candidateModule.setResumeLockDialogOpen}
         >
            <Dialog.Content aria-describedby={undefined} maxWidth="520px">
               <Dialog.Title aria-describedby={undefined}>
               </Dialog.Title>
               <ResumeLockScreen
                  missingFields={candidateModule.missingResumeProfileFields}
                  mode="dialog"
               />
            </Dialog.Content>
         </Dialog.Root>
      </>
   );
};

export const CandidateRoutesGuard = observer(CandidateRoutesGuardView);
